import type { FrontendDownloadFileHandle } from "src/api/generated/common/frontendDownloadFileHandle.ts";
import { SalvosCustomerOrderApi } from "src/api/generated/tenants/salvos/erp/sales/customerOrder/salvosCustomerOrderApi.ts";
import type { SalvosBillingPlanSumCheck } from "src/api/generated/tenants/salvos/erp/sales/customerOrder/salvosBillingPlanSumCheck.ts";
import type { PrintCustomerOrderConfirmationParams } from "src/tenantCustomizations/TenantCustomizations.ts";
import { SalvosBillingPlanSumDifferenceContent } from "src/tenantCustomizations/tenants/salvos/sales/SalvosBillingPlanSumDifferenceContent.tsx";

export async function printSalvosCustomerOrderConfirmation({
	customerOrderId,
	showConfirmDialog,
	showMessageDialog,
}: PrintCustomerOrderConfirmationParams): Promise<FrontendDownloadFileHandle | undefined> {
	let result = await SalvosCustomerOrderApi.printCustomerOrderConfirmationWithBillingPlanCheck({
		customerOrderId,
		allowWithoutBillingPlanComparison: false,
	});

	const confirmationMessage = getContinueWithoutComparisonMessage(result.billingPlanCheck);
	if (confirmationMessage != null) {
		const confirmed = await showConfirmDialog({
			title: "Tilausvahvistus",
			message: confirmationMessage,
			confirmButtonText: "Jatka",
			cancelButtonText: "Peruuta",
		});
		if (!confirmed) return undefined;

		// The server checks the sums again, so continuing cannot bypass a difference detected meanwhile.
		result = await SalvosCustomerOrderApi.printCustomerOrderConfirmationWithBillingPlanCheck({
			customerOrderId,
			allowWithoutBillingPlanComparison: true,
		});
	}

	if (result.billingPlanCheck.status === "SUMS_DIFFER") {
		showMessageDialog({
			title: "Tilausvahvistusta ei voi muodostaa",
			content: <SalvosBillingPlanSumDifferenceContent check={result.billingPlanCheck} />,
			closeButtonText: "Sulje",
		});
		return undefined;
	}

	return result.fileHandle ?? undefined;
}

function getContinueWithoutComparisonMessage(check: SalvosBillingPlanSumCheck): string | undefined {
	switch (check.status) {
		case "NO_BILLING_PLAN_LINES":
			return (
				"Myyntitilauksella ei ole laskutussuunnitelmarivejä, joiden summan voisi tarkistaa. " +
				"Haluatko muodostaa tilausvahvistuksen ilman summien vertailua?"
			);
		case "BILLING_PLAN_DISABLED":
			return (
				"Myyntitilauksella ei ole laskutussuunnitelmaa käytössä. Verollisten summien vertailua ei tehdä. " +
				"Haluatko silti muodostaa tilausvahvistuksen?"
			);
		default:
			return undefined;
	}
}
